import type { CSSProperties, JSX, ReactNode } from "react";
import { cx } from "../cx";

/*
 * The double-turnstile mark vector, embedded from
 * design/tarski-kit/assets/tarski-mark-blue.svg (colored via currentColor).
 * Brand rule: always this vector, never the Unicode glyph.
 */
const MARK_PATH =
  "M 449.93 369.14 L 449.93 385.60 A 0.61 0.61 0.0 0 1 449.32 386.21 L 299.17 386.21 A 0.61 0.61 0.0 0 0 298.56 386.82 L 298.56 450.52 A 0.61 0.61 0.0 0 1 297.95 451.13 L 281.31 451.13 A 0.61 0.61 0.0 0 1 280.70 450.52 L 280.70 255.26 A 0.61 0.61 0.0 0 1 281.31 254.65 L 297.94 254.65 A 0.61 0.61 0.0 0 1 298.55 255.26 L 298.55 326.20 A 0.61 0.61 0.0 0 0 299.16 326.81 L 449.36 326.81 A 0.61 0.61 0.0 0 1 449.97 327.42 L 449.97 343.98 A 0.61 0.61 0.0 0 1 449.36 344.59 L 299.16 344.59 A 0.61 0.61 0.0 0 0 298.55 345.20 L 298.55 367.92 A 0.61 0.61 0.0 0 0 299.16 368.53 L 449.32 368.53 A 0.61 0.61 0.0 0 1 449.93 369.14 Z";

export interface TurnstileMarkProps {
  /** Mark height in px; width derives from the glyph ratio. */
  size?: number | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/** The double-turnstile mark as an inline vector, colored via currentColor. */
export function TurnstileMark({ size = 14, className, style }: TurnstileMarkProps): JSX.Element {
  return (
    <svg
      width={size * (170 / 197)}
      height={size}
      viewBox="280 254 170 197"
      fill="currentColor"
      className={cx("tk-turnstile-mark", className)}
      style={style}
      aria-hidden="true"
    >
      <path d={MARK_PATH} />
    </svg>
  );
}

export interface VerifiedLineProps {
  /** Status text (default "all invariants hold"). */
  children?: ReactNode | undefined;
  /** Trailing muted metadata, e.g. "deployed in 1.2s". */
  meta?: string | undefined;
  /** Dark chip treatment (default) vs inline-on-light. */
  dark?: boolean | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * The earned turnstile status line — CLI-style verified output. Use only for
 * genuinely verified states, never decoration. Ported from
 * design/tarski-kit/components/display/VerifiedLine.jsx.
 */
export function VerifiedLine({
  children = "all invariants hold",
  meta,
  dark = true,
  className,
  style,
}: VerifiedLineProps): JSX.Element {
  return (
    <div
      className={cx("tk-verified-line", !dark && "tk-verified-line--light", className)}
      style={style}
    >
      <TurnstileMark size={13} />
      <span>{children}</span>
      {meta === undefined ? null : <span className="tk-verified-line__meta">· {meta}</span>}
    </div>
  );
}
